import { useSyncExternalStore } from 'react'
import api from 'helpers/api'
import { useAuth } from 'hooks/auth'
import type { IUserObject } from 'types'
import {
  adaptColourToTheme,
  adaptFontColourToTheme,
  adaptHeaderColoursToTheme,
  adaptTextColourToSurface
} from 'helpers/colour'
import {
  DARK_MEDIA_QUERY,
  ResolvedTheme,
  ThemePreference,
  applyTheme,
  readStoredTheme,
  resolveTheme,
  storeTheme
} from 'helpers/theme'

/**
 * Theme lives in a module-level store read through useSyncExternalStore rather
 * than in a context.
 *
 * Two reasons. A context provider would have to sit above the router to cover
 * the whole app, and CLAUDE.md warns off putting the React Compiler near data
 * context providers. And the theme is genuinely global, single-valued state that
 * the document already holds — a store keeps the <html> class and the React
 * state as one thing instead of two that can drift.
 */

let preference: ThemePreference = readStoredTheme()
const listeners = new Set<() => void>()

const emit = (): void => listeners.forEach((listener) => listener())

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getPreference = (): ThemePreference => preference

/**
 * Apply a preference locally: document class, localStorage, subscribers.
 *
 * localStorage is a *cache*, not the record. The record is the user's profile,
 * but the inline script in index.html has to decide what to paint before any
 * request could return, so it reads the last known value from here instead.
 */
export const setThemePreference = (next: ThemePreference): void => {
  if (preference === next) return
  preference = next
  storeTheme(next)
  applyTheme(resolveTheme(next))
  emit()
}

/**
 * Adopt the preference stored on the signed-in user's profile.
 *
 * Called from the auth provider whenever a user object arrives — sign-in,
 * /user/me, a token refresh. The profile wins over whatever this browser had
 * cached, which is the whole point: set dark on a laptop and the desktop picks
 * it up on next load rather than keeping its own stale answer.
 *
 * Deliberately does not write back to the API. The value came from there, and a
 * write here would turn every page load into a redundant PUT.
 */
/**
 * At most one theme write is in flight at a time, and the newest value wins.
 *
 * Firing a PUT per click lets them reach the server out of order: a quick
 * Light -> Dark can land Dark first and leave the profile on Light for every
 * other device. Chaining them into a queue fixes the ordering but wedges
 * permanently if one request never settles — every later click then queues
 * behind a promise that will not resolve. Coalescing gives the ordering
 * guarantee without the unbounded chain: whatever the person last chose is sent
 * as soon as the current request finishes, and the clicks in between are simply
 * superseded.
 */
let inFlight = false

/**
 * A write waiting for the one in flight to finish. It carries its owner: a
 * queued value belongs to whoever asked for it, and replaying it after the
 * account has changed would write one person's choice into another's profile.
 */
let queuedWrite: { value: ThemePreference; owner: string | undefined } | null = null

/** Who is signed in now. Every path that sets the user must keep this current. */
let currentUserId: string | undefined

/** Read the owner back. Exported for tests — nothing in the app needs it. */
export const getThemeOwnerForTest = (): string | undefined => currentUserId

export const setThemeOwner = (userId: string | undefined): void => {
  if (userId === currentUserId) return
  currentUserId = userId
  // Whatever was queued belonged to the previous account.
  queuedWrite = null
}

export const hydrateThemeFromProfile = (profileTheme: ThemePreference | undefined): void => {
  if (!profileTheme) return
  setThemePreference(profileTheme)
}

/**
 * Re-resolve when the OS flips while we're following it. Registered once at
 * module scope so the listener exists whether or not a component is mounted —
 * the document class has to stay right regardless.
 */
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const query = window.matchMedia(DARK_MEDIA_QUERY)
  const onSystemChange = (): void => {
    if (preference !== 'system') return
    applyTheme(resolveTheme(preference))
    emit()
  }
  // Safari below 14 only has the deprecated form.
  if (typeof query.addEventListener === 'function') query.addEventListener('change', onSystemChange)
  else query.addListener(onSystemChange)
}

interface UseThemeResult {
  /** What the customer chose, including `system`. This is what the UI ticks. */
  preference: ThemePreference
  /** What is actually painted right now. */
  resolved: ResolvedTheme
  setPreference: (preference: ThemePreference) => void
}

/**
 * Bind each write to the person who made it. Two things can invalidate a
 * response before it lands: they click again (so it is stale), or they sign out
 * and someone else signs in — and merging the previous account's profile into
 * the new session would be worse than a wrong theme.
 */
const send = (
  value: ThemePreference,
  requestedBy: string | undefined,
  updateUser: (info: IUserObject) => Promise<void>
): void => {
  inFlight = true
  // Whether the server actually took this value. A queued write for the *same*
  // value is redundant after a success, but after a failure it is the retry —
  // skipping it would leave the profile permanently behind the UI.
  let delivered = false

  api
    .updateUserTheme(value)
    .then((response) => {
      delivered = true
      if (getPreference() !== value) return
      if (currentUserId !== requestedBy) return
      return updateUser(response.data)
    })
    .catch(() => {
      // Left deliberately silent. The theme is applied and cached; the only
      // thing lost is the cross-device sync, and a snackbar about it would be
      // noise on a setting the person can plainly see has taken effect.
    })
    .finally(() => {
      inFlight = false

      const pending = queuedWrite
      queuedWrite = null
      if (!pending) return
      if (pending.owner !== currentUserId) return
      if (pending.value === value && delivered) return

      send(pending.value, pending.owner, updateUser)
    })
}

// The server snapshot is only reached in tests; `system` is the honest answer
// when there is no window to ask.
const useThemePreference = (): ThemePreference =>
  useSyncExternalStore(subscribe, getPreference, () => 'system' as ThemePreference)

/**
 * The appearance control. Persists to the profile as well as locally, so the
 * choice follows the user to their next device.
 */
export const useTheme = (): UseThemeResult => {
  const current = useThemePreference()
  const { user, updateUser } = useAuth()

  const setPreference = (next: ThemePreference): void => {
    // Paint first. The local change is what the person just asked for, and it
    // should not wait on — or be undone by — a round trip.
    setThemePreference(next)

    if (!user) return

    if (inFlight) {
      queuedWrite = { value: next, owner: user.publicId }
      return
    }
    send(next, user.publicId, updateUser)
  }

  return {
    preference: current,
    resolved: resolveTheme(current),
    setPreference
  }
}

/**
 * Just the painted theme, for the many components that only need to know which
 * way round the palette is — typically to adapt a customer-chosen colour.
 *
 * Reads the store directly rather than going through useTheme, so the grid's hot
 * components don't subscribe to the auth context and re-render with it.
 *
 * Its snapshot is the resolved theme, not the preference: while following the
 * system, an OS flip leaves the preference at `system`, and a snapshot that
 * doesn't change never re-renders anything.
 */
export const useResolvedTheme = (): ResolvedTheme =>
  useSyncExternalStore(
    subscribe,
    () => resolveTheme(getPreference()),
    () => resolveTheme('system')
  )

/**
 * Adapts customer-chosen colours to the painted theme.
 *
 * This is deliberately applied where a colour becomes a style rather than inside
 * the cell-colouring helpers. Those helpers are pure, already take nine
 * positional arguments, and run in the grid's hottest loops; threading a tenth
 * through sixteen call sites to carry the theme would be both noisy and easy to
 * get half-right. Adapting at the boundary keeps the stored colour as the single
 * source of truth right up to the point it is painted.
 *
 * Values that aren't hex — `inherit`, `none`, a `var(--color-…)` fallback — pass
 * through untouched, so it is safe to wrap anything the helpers return.
 */
export const useThemedColours = (): {
  fill: (colour: string | null | undefined) => string | undefined
  text: (fontColour: string | null | undefined, background: string | null | undefined) => string | undefined
  onSurface: (colour: string | null | undefined) => string | undefined
  header: (
    background: string | null | undefined,
    text: string | null | undefined
  ) => { backgroundColor: string; color: string }
} => {
  const theme = useResolvedTheme()

  return {
    fill: (colour) => (colour ? adaptColourToTheme(colour, theme) : undefined),
    // For text with no fill behind it — document headings and the like — where
    // the job is legibility against the page, not separation from it.
    onSurface: (colour) => (colour ? adaptTextColourToSurface(colour, theme) : undefined),
    // The font colour is judged against the *adapted* fill, not the stored one —
    // that is the surface it will actually sit on.
    text: (fontColour, background) =>
      fontColour ? adaptFontColourToTheme(fontColour, adaptColourToTheme(background || '', theme), theme) : undefined,
    // A column header's stored colours, painted. Headers still holding the
    // defaults follow the theme's tokens; see adaptHeaderColoursToTheme.
    header: (background, text) => {
      const adapted = adaptHeaderColoursToTheme(background, text, theme)
      return { backgroundColor: adapted.background, color: adapted.text }
    }
  }
}
